import { useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { castVote } from '../api/client';

interface VoteButtonsProps {
  kind: 'topic' | 'comment';
  id: string;
  score: number;
  myVote: 1 | -1 | null;
  /** Query keys to refetch once the vote lands, rather than threading optimistic state through props. */
  invalidateKeys: readonly QueryKey[];
}

export function VoteButtons({
  kind,
  id,
  score,
  myVote,
  invalidateKeys,
}: VoteButtonsProps): React.JSX.Element {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (value: 1 | -1) => castVote(kind, id, value),
    onSuccess: async () => {
      await Promise.all(invalidateKeys.map((key) => queryClient.invalidateQueries({ queryKey: key })));
    },
  });

  return (
    <div className="vote-buttons" role="group" aria-label="Vote on this post">
      <button
        type="button"
        className="vote-button"
        aria-pressed={myVote === 1}
        disabled={mutation.isPending}
        onClick={() => mutation.mutate(1)}
        data-testid={`upvote-${kind}-${id}`}
      >
        <span aria-hidden="true">▲</span>
        <span className="visually-hidden">Upvote</span>
      </button>

      <span className="vote-score" data-testid={`score-${kind}-${id}`}>
        {score}
      </span>

      <button
        type="button"
        className="vote-button"
        aria-pressed={myVote === -1}
        disabled={mutation.isPending}
        onClick={() => mutation.mutate(-1)}
        data-testid={`downvote-${kind}-${id}`}
      >
        <span aria-hidden="true">▼</span>
        <span className="visually-hidden">Downvote</span>
      </button>

      {mutation.error && (
        <p role="alert" className="hint">
          Could not record that vote.
        </p>
      )}
    </div>
  );
}
